
import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faEnvelope, faUnlockAlt } from "@fortawesome/free-solid-svg-icons";
import { faFacebookF, faGithub, faTwitter } from "@fortawesome/free-brands-svg-icons";
import { Col, Row, Form, Card, Button, FormCheck, Container, InputGroup, Alert } from '@themesberg/react-bootstrap';
import { Link, useLocation } from 'react-router-dom';

import { Routes } from "../../routes";
import BgImage from "../../assets/img/illustrations/signin3.svg";
import GeotechSVG from "../../assets/img/illustrations/geotechscience.svg";
import GeoNowLogo from "../../assets/img/brand/geonow/GeoNow_WithText_500w.png"

import swal from 'sweetalert';
import { useSelector, useDispatch } from 'react-redux';
import { doSignIn, doSignOut, doSetJoinedProjects, doSetCurrentProjectId, doClearProjectState } from './../../actions';
import { VERSION, BUILD_DATE } from '../../version';
import geoNow from '../../utils/geonow';
/*import { UserContext } from './contexts/UserContext'*/


//async function loginUser(credentials) {
//    return fetch(process.env.REACT_APP_WEBAPI_HOST + "/Users/Authenticate", {
//        method: 'POST',
//        headers: {
//            'Content-Type': 'application/json'
//        },
//        body: JSON.stringify(credentials)
//    })
//        .then(data => data.json())
//}

/**
 * @typedef {Object} ApiMessageResponse
 * @property {string} [message] The message returned by WebApi.
 */

/**
 * @typedef {Object} ApiWarning
 * @property {string} [message] The warning message returned by WebApi.
 */

/**
 * Gets a usable message from a WebApi response.
 * @param {ApiMessageResponse|null|undefined} response The WebApi response.
 * @param {string} fallbackMessage The message used when WebApi returned no message.
 * @returns {string} The message to display.
 */
const getApiMessage = (response, fallbackMessage) => {
    if (typeof response?.message === 'string' && response.message.trim() !== '') {
        return response.message;
    }
    return fallbackMessage;
};

/**
 * Formats WebApi project warnings for display.
 * @param {Array<string|ApiWarning>|null|undefined} warnings The warnings returned by WebApi.
 * @returns {string} A numbered, display-ready warning message.
 */
const getWarningMessage = (warnings) => {
    if (!Array.isArray(warnings) || warnings.length === 0) {
        return '';
    }

    const warningLines = warnings.slice(0, 10).map((warning, index) => {
        const message = typeof warning === 'string' ? warning : warning?.message;
        return `${index + 1}. ${message || '未知的專案資料異常。'}`;
    });

    if (warnings.length > warningLines.length) {
        warningLines.push(`另有 ${warnings.length - warningLines.length} 筆警告，請查看後端記錄。`);
    }

    return warningLines.join('\n');
};

export default () => {

    const user = useSelector(state => state.user);
    const dispatch = useDispatch();
    const location = useLocation();
    const autoDebugLogin = process.env.NODE_ENV === 'development' && new URLSearchParams(location.search).get('manual') !== '1';
    const [checkingDebugLogin, setCheckingDebugLogin] = useState(autoDebugLogin);
    const [debugLoginError, setDebugLoginError] = useState('');
    const loginHandler = useRef(null);

    const [username, setUserName] = useState();
    const [password, setPassword] = useState();
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [webapiVersionInfo, setWebapiVersionInfo] = useState({ version: '', buildDate: '', timeZone: '' });

    const signin = (user) => dispatch(doSignIn(user));

    useEffect(() => {
        const fetchVersionInfo = async () => {
            try {
                const response = await geoNow.api.siteInfo.getPublicSiteInfo();
                if (response) {
                    const { ver, now, tz } = response;
                    setWebapiVersionInfo({
                        version: ver,
                        buildDate: now,
                        timeZone: tz
                    });
                }
            } catch (error) {
                console.error('Error fetching version info:', error);
            }
        };

        fetchVersionInfo();
    }, []);


    /**
     * Authenticates the user and loads a fresh project list before committing login state.
     * @param {React.FormEvent<HTMLFormElement>} e The sign-in form submit event.
     * @returns {Promise<void>}
     */
    const handleSubmit = async (e, debugResponse = null, isCancelled = () => false) => {
        e?.preventDefault();
        setIsLoggingIn(true);
        let authenticationSucceeded = false;

        dispatch(doClearProjectState());
        dispatch(doSignOut());
        geoNow.setToken(null);

        try {
            const res_auth = debugResponse || await geoNow.api.user.loginUser({
                username,
                password
            });

            if (!res_auth || !res_auth.token) {
                throw new Error(getApiMessage(res_auth, '帳號或密碼錯誤，或登入服務未回傳有效憑證。'));
            }
            authenticationSucceeded = true;

            const res_uip_list = await geoNow.api.project.getJoinedProjects(res_auth.token, res_auth.id);
            if (isCancelled()) return;
            if (!res_uip_list || res_uip_list.status !== 1) {
                const warningMessage = getWarningMessage(res_uip_list?.warnings);
                const errorMessage = getApiMessage(res_uip_list, 'WebApi 無法載入專案清單。');
                throw new Error(warningMessage ? `${errorMessage}\n\n${warningMessage}` : errorMessage);
            }

            if (!Array.isArray(res_uip_list.message)) {
                throw new Error('WebApi 回傳的專案清單格式不正確，請聯絡系統管理員。');
            }

            const joinedProjects = res_uip_list.message;
            const defaultPid = geoNow.stateHelper.project.getDefaultProjectIdFromUipList(joinedProjects)
                ?? joinedProjects[0]?.projectId
                ?? null;

            signin(res_auth);
            geoNow.setToken(res_auth.token);
            dispatch(doSetJoinedProjects(joinedProjects));
            dispatch(doSetCurrentProjectId(defaultPid));

            const warningMessage = getWarningMessage(res_uip_list.warnings);
            if (warningMessage) {
                await swal({
                    title: '登入成功，但部分專案未載入',
                    text: warningMessage,
                    icon: 'warning',
                    button: '繼續'
                });
            }
            else if (joinedProjects.length === 0) {
                await swal({
                    title: '登入成功',
                    text: '此帳號目前沒有被指派任何專案。',
                    icon: 'warning',
                    button: '繼續'
                });
            }
            else if (!debugResponse) {
                await swal("Success", "登入成功!", "success", {
                    buttons: false,
                    timer: 2000,
                });
            }

            window.location.href = "#" + Routes.DashboardOverview.path;
        }
        catch (error) {
            if (isCancelled()) return;
            dispatch(doSignOut());
            dispatch(doClearProjectState());
            geoNow.setToken(null);

            await swal({
                title: authenticationSucceeded ? '專案清單載入失敗' : '登入失敗',
                text: error?.message || '發生未知錯誤，請稍後再試。',
                icon: 'error',
                button: '確定'
            });
        }
        finally {
            setIsLoggingIn(false);
        }
    }

    loginHandler.current = handleSubmit;
    useEffect(() => {
        let cancelled = false;
        if (!autoDebugLogin) { setCheckingDebugLogin(false); return; }
        setCheckingDebugLogin(true);
        const tryDebugLogin = async () => {
            try {
                const response = await geoNow.api.user.debugLogin();
                if (!cancelled && response?.token) await loginHandler.current(null, response, () => cancelled);
            } catch (error) {
                if (!cancelled) setDebugLoginError(error.message || 'Debug 自動登入失敗，請使用一般登入。');
            } finally { if (!cancelled) setCheckingDebugLogin(false); }
        };
        tryDebugLogin();
        return () => { cancelled = true; };
    }, [autoDebugLogin]);

    return (
        <main>
            {/*<UserContext.Provider value={this.state}>*/}
            {/*</UserContext.Provider>*/}
            <section className="d-flex align-items-center my-5 mt-lg-6 mb-lg-5">
                <Container>
                    <p className="text-center">
                        <Card.Link as={Link} to={Routes.DashboardOverview.path} className="text-gray-700">
                            <FontAwesomeIcon icon={faAngleLeft} className="me-2" /> 回到首頁
                        </Card.Link>
                    </p>
                    <Row className="justify-content-center form-bg-image" style={{ backgroundImage: `url(${BgImage})` }}>
                        <Col xs={12} className="d-flex align-items-center justify-content-center">
                            <div id="loginPanel" className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 fmxw-500" >
                                <div className="text-center text-md-center mb-4 mt-md-0">
                                    {/*<div style={{ minHeight: 60, maxHeight: 90, backgroundImage: `url(${GeoNowSVG})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}> </div>*/}
                                    <img src={GeoNowLogo} />
                                    
                                    {/*<h4 className="mb-0"><div style={{ minHeight: 50, maxHeight: 90, backgroundImage: `url(${GeotechSVG})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', display: 'inline-block', minWidth: 50 }}></div>捷儀科技</h4>*/}
                                    
                                    
                                </div>
                                <Form className="mt-4" onSubmit={handleSubmit}>
                                    <Form.Group id="email" className="mb-4">
                                        <Form.Label>信箱</Form.Label>
                                        <InputGroup>
                                            <InputGroup.Text>
                                                <FontAwesomeIcon icon={faEnvelope} />
                                            </InputGroup.Text>
                                            <Form.Control autoFocus required type="email" placeholder="example@gmail.com" onChange={e => setUserName(e.target.value)} />
                                        </InputGroup>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Group id="password" className="mb-4">
                                            <Form.Label>密碼</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text>
                                                    <FontAwesomeIcon icon={faUnlockAlt} />
                                                </InputGroup.Text>
                                                <Form.Control required type="password" placeholder="Password" onChange={e => setPassword(e.target.value)} />
                                            </InputGroup>
                                        </Form.Group>
                                        {false && (
                                            <div className="d-flex justify-content-between align-items-center mb-4">
                                                {/*<Form.Check type="checkbox">*/}
                                                {/*    <FormCheck.Input id="defaultCheck5" className="me-2" />*/}
                                                {/*    <FormCheck.Label htmlFor="defaultCheck5" className="mb-0">記住我</FormCheck.Label>*/}
                                                {/*</Form.Check>*/}
                                                <Card.Link className="small text-end">忘記密碼?</Card.Link>
                                            </div>
                                        )}
                                    </Form.Group>
                                    {debugLoginError && <Alert variant="warning">{debugLoginError}</Alert>}
                                    <Button variant="primary" type="submit" className="w-100" disabled={isLoggingIn || checkingDebugLogin}>
                                        {checkingDebugLogin ? 'Debug 自動登入中…' : !isLoggingIn ? "登入" : "登入中..."}
                                    </Button>
                                    {process.env.NODE_ENV === 'development' && <div className="text-center mt-2">
                                        <Link to={Routes.Signin.path + '?manual=1'}>改用一般帳密登入</Link>
                                    </div>}
                                    <div className="text-center small text-muted" style={{
                                        position: 'relative',
                                        top: '2rem',
                                    }}>
                                        <span style={{ color: '#c1bfcd' }}>{VERSION} / {webapiVersionInfo.version}</span>
                                        {/*<ul className="list-unstyled ">*/}
                                        {/*    <li className="mt-1 mb-1">*/}
                                        {/*        <span className="fw-normal">前端版本: {VERSION}</span>*/}
                                        {/*    </li>*/}
                                        {/*    <li className="mt-1 mb-1">*/}
                                        {/*        <span className="fw-normal">後端版本: {webapiVersionInfo.version}</span>*/}
                                        {/*    </li>*/}
                                        {/*</ul>*/}
                                    </div>
                                    {/*<div className="mt-3 mb-4 text-center">*/}
                                    {/*    <span className="fw-normal">前端版本: {VERSION}*/}{/* ({BUILD_DATE})*/}{/*</span>*/}
                                    {/*    */}{/*<div>*/}
                                    {/*    */}{/*    <p>Version: {versionInfo.version}</p>*/}
                                    {/*    */}{/*    <p>Build Date: {versionInfo.buildDate}</p>*/}
                                    {/*    */}{/*    <p>Time Zone: {versionInfo.timeZone}</p>*/}
                                    {/*    */}{/*</div>*/}
                                    {/*</div>*/}
                                    {/*<div className="mt-3 mb-4 text-center">*/}
                                    {/*    <span className="fw-normal">後端版本: {webapiVersionInfo.version}*/}{/* ({versionInfo.buildDate})*/}{/*</span>*/}
                                    {/*</div>*/}
                                </Form>

                                {/*<div className="mt-3 mb-4 text-center">*/}
                                {/*  <span className="fw-normal">or login with</span>*/}
                                {/*</div>*/}
                                {/*<div className="d-flex justify-content-center my-4">*/}
                                {/*  <Button variant="outline-light" className="btn-icon-only btn-pill text-facebook me-2">*/}
                                {/*    <FontAwesomeIcon icon={faFacebookF} />*/}
                                {/*  </Button>*/}
                                {/*  <Button variant="outline-light" className="btn-icon-only btn-pill text-twitter me-2">*/}
                                {/*    <FontAwesomeIcon icon={faTwitter} />*/}
                                {/*  </Button>*/}
                                {/*  <Button variant="outline-light" className="btn-icon-only btn-pil text-dark">*/}
                                {/*    <FontAwesomeIcon icon={faGithub} />*/}
                                {/*  </Button>*/}
                                {/*</div>*/}

                                {false && (
                                    <div className="d-flex justify-content-center align-items-center mt-4">
                                        <span className="fw-normal">
                                            還沒有帳號?
                                            <Card.Link as={Link} to={Routes.Signup.path} className="fw-bold">
                                                {` 建立一個 `}
                                            </Card.Link>
                                        </span>
                                    </div>
                                )}

                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </main>
    );
};
